<template>
  <div class="linechart" @mousemove="onMouseMove($event)">

    <div class="linechart__svg" :id="'linechart--' + this._uid">
      <svg :id="'linechart__svg--' + this._uid" xmlns="http://www.w3.org/2000/svg" version="1.1" xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:svgjs="http://svgjs.com/svgjs" viewBox="0 0 740 285">
        <g class="questionmark"><path class="cls-2" d="M5.66,10.82a5.16,5.16,0,1,1,5.16-5.16A5.17,5.17,0,0,1,5.66,10.82Z"/><path class="cls-3" d="M5.48,7.25a.59.59,0,1,0,.57.6A.57.57,0,0,0,5.48,7.25Z"/><path class="cls-3" d="M5.59,3a2.07,2.07,0,0,0-1.14.32l-.08.06.27.72L4.78,4a1.2,1.2,0,0,1,.68-.21c.27,0,.59.1.59.52a1.55,1.55,0,0,1-.49.94,1.83,1.83,0,0,0-.49,1.38l0,.31h.83V6.6a1.36,1.36,0,0,1,.4-1A2.13,2.13,0,0,0,7,4.21,1.23,1.23,0,0,0,5.59,3Z"/></g>
      </svg>
    </div>

    <transition name="fade">
      <div
        class="linechart__tooltip"
        :style="{
            left: tooltipX + 'px',
            top: tooltipY + 'px'
          }"
        v-if="(tooltipCurrent !== false)"
      >
        <span class="linechart__tooltip__name">{{ tooltipCurrent.name }}</span>
        <span class="linechart__tooltip__val">{{ tooltipCurrent.value }}</span>
      </div>
    </transition>

  </div>
</template>

<script>

/**
 * Data for the line chart is accepted in this format
 * [{
 *   name: 'the attribute name',
 *   color: 'yellow',
 *   yearData: {
 *     2005: { point: 0.5, high: 0.6, low: 0.45 },
 *     2006: { point: 0.2, high: 0.25, low: 0 }
 *   }
 * },
 * {
 *   name: 'another attribute name',
 *   color: 'red',
 *   yearData: {
 *     2005: { point: 0.5, high: 0.6, low: 0.45 },
 *     2006: { point: 0.2, high: 0.25, low: 0 }
 *   }
 * }]
 */

// import Vue from 'vue'
import SVG from 'svg.js'
import _ from 'lodash'

import HelpTooltip from '../misc/HelpTooltip'

import {EventBus} from '@/event-bus.js'

export default {
  name: 'LineChart',
  components: { HelpTooltip },
  props: {
    data: {
      type: Array,
      required: true
    },
    visibleRange: {
      type: Array,
      default: [1980, 2005]
    },
    showConfidenceInterval: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    firstYear () {
      return this.visibleRange[0]
    },
    lastYear () {
      return this.visibleRange[1]
    }
  },
  data () {
    return {
      canvas: false,
      canvasSize: [740, 285],
      labelFontsize: 11,
      hoverSize: 30,
      hoverGroup: [],
      gridGroup: false,
      groups: {},
      sideSpace: 40,
      bigStrokeWidth: 20,
      strokeWidth: 2,
      yLabels: ['0', '.1', '.2', '.3', '.4', '.5', '.6', '.7', '.8', '.9', '1'],
      tooltipCurrent: false,
      tooltipX: 0,
      tooltipY: 0
    }
  },
  watch: {
    data () {
      this.redraw()
    },
    showConfidenceInterval () {
      this.redraw()
    },
    visibleRange () {
      this.redraw()
    }
  },
  mounted () {
    this.drawCanvas()
    this.drawChart()

    EventBus.$on('window:resize', () => {
      this.setChartSize()
    })
  },
  destroyed () {
    EventBus.$emit('tooltip.hide')
  },
  methods: {

    /**
     * Redraws the chart
     */
    redraw () {
      this.reset()
      this.drawChart()
    },

    /**
     * Draws the chart
     */
    drawChart () {
      if (!this.data) return false

      // Initialize
      this.drawGrid()

      _.each(this.data, (val, index) => {
        this.groups[index] = this.drawDataLine(val)
      })

      this.drawOpacityBoxes()
      this.groups.labels = this.drawLabels()

      // Add hover
      _.each(this.data, (val) => {
        this.addHover(val)
      })

      this.addYAxisExplainer()
    },

    /**
     * Removes all elements from the chart
     */
    reset () {
      this.gridGroup.remove()
      _.each(this.hoverGroup, (group) => {
        group.remove()
      })
      this.hoverGroup = []
      _.forIn(this.groups, (group) => {
        group.remove()
      })
      this.groups = {}
    },

    /**
     * Initialize the SVG convas
     */
    drawCanvas () {
      this.canvas = SVG('linechart__svg--' + this._uid)
      this.setChartSize()
    },

    /**
     * Set the chart size
     */
    setChartSize () {
      this.canvas.attr('display', 'none')

      let containerEl = document.getElementsByClassName('linechart')
      let containerWidth = (containerEl[0]) ? containerEl[0].offsetWidth : 0

      this.canvas.attr('display', 'block')

      let dimension = this.canvasSize[0] / this.canvasSize[1]
      let w = containerWidth + 40 // add 40 because the wrapper has -20px margin
      let h = (w / dimension)

      this.canvas
        .size(w, h)
        .viewbox(0, 0, this.canvasSize[0], this.canvasSize[1])
        .style({ height: h + 'px', width: w + 'px' })
    },

    /**
     * Draws the grid
     */
    drawGrid () {
      let yearXMap = this.getYearXMap()
      let canvasHeight = this.getCalculatedCanvasHeight()
      var offset = this.getVerticalOffset()

      // Draw horizontal grid
      this.gridGroup = this.canvas.group()
      _.each(this.yLabels, (val) => {
        let yPos = canvasHeight * parseFloat(val) + offset
        let faded = ((parseFloat(val) * 10) % 2 === 0) ? '' : ' svg-stroke--faded'
        var line = this.drawLine(0, yPos, this.canvasSize[0], yPos, 'svg-stroke--gray-lightest' + faded)
        this.gridGroup.add(line)
      })

      // Draw vertical grid
      _.each(yearXMap, (xPos, year) => {
        let strokeWidth = (year % 5 === 0) ? 2 : 0.4
        let line = this.drawLine(
          xPos,
          offset,
          xPos,
          canvasHeight + offset,
          'svg-stroke--gray-lightest',
          strokeWidth
        )
        this.gridGroup.add(line)
      })
    },

    getDataYearPoint (year, row) {
      let offset = this.getVerticalOffset()
      let point = parseFloat(row.point)

      if (point === undefined || isNaN(point)) return false

      let x = this.getYearXMap()[year]
      let y = this.getCalculatedCanvasHeight() * (1 - parseFloat(point)) + offset

      return [x, y]
    },

    getDataYearConfHighPoint (year, row) {
      let canvasHeight = this.getCalculatedCanvasHeight()
      let offset = this.getVerticalOffset()
      let high = parseFloat(row.high)

      if (high === undefined || isNaN(high)) return false
      if (high > 1) high = 1

      let x = this.getYearXMap()[year]
      let y = canvasHeight * (1 - parseFloat(high)) + offset

      return [x, y]
    },

    getDataYearConfLowPoint (year, row) {
      let canvasHeight = this.getCalculatedCanvasHeight()
      let offset = this.getVerticalOffset()
      let low = parseFloat(row.low)

      if (low === undefined || isNaN(low)) return false
      if (low < 0) low = 0

      let x = this.getYearXMap()[year]
      let y = canvasHeight * (1 - parseFloat(low)) + offset

      return [x, y]
    },

    getYearCoordinates (data) {
      let years = {}
      let yearXMap = this.getYearXMap()

      _.each(data.yearData, (val, year) => {
        year = parseInt(year)
        years[year] = {}

        let xy = this.getDataYearPoint(year, val)
        let highXy = this.getDataYearConfHighPoint(year, val)
        let lowXy = this.getDataYearConfLowPoint(year, val)

        years[year].point = _.has(yearXMap, year) ? xy : false
        years[year].high = _.has(yearXMap, year) ? highXy : false
        years[year].low = _.has(yearXMap, year) ? lowXy : false
      })

      return years
    },

    getLineDataArray (values, key) {
      let lines = []
      let lineIndex = 0
      _.each(values, (val, year) => {
        if (val[key] !== false) {
          if (!lines[lineIndex]) {
            lines[lineIndex] = []
          }

          lines[lineIndex].push(val[key])
        } else {
          lineIndex++
        }
      })
      return _.compact(lines)
    },

    /**
     * Draws a data polyline based on data
     * @param  {object} data The line data
     * @return {Object}      A svg group
     */
    drawDataLine (data) {
      let group = this.canvas.group()
      let coordinateArrays = this.getYearCoordinates(data)
      let lines = this.getLineDataArray(coordinateArrays, 'point')

      _.each(lines, (lineData) => {
        // Draw points
        let pointsLine = this.drawPolyline(lineData, 'svg-stroke--' + data.color, this.strokeWidth)
        group.add(pointsLine)
      })

      if (this.showConfidenceInterval) {
        let highLines = this.getLineDataArray(coordinateArrays, 'high')
        let lowLines = this.getLineDataArray(coordinateArrays, 'low')

        _.each(highLines, (highData, index) => {
          if (!lowLines[index]) return false
          let lowData = lowLines[index].reverse()

          let confidenceData = highData.concat(lowData)

          // Draw points
          let condidenceLine = this.canvas.polygon(confidenceData).addClass('svg-fill--' + data.color + ' svg-fill--faded-2').stroke({ width: 0 })
          group.add(condidenceLine)
        })
      }

      return group
    },

    /**
     * Adds hover functionality
     * @param  {object} data The line data
     */
    addHover (data) {
      let yearXMap = this.getYearXMap()
      let canvasHeight = this.getCalculatedCanvasHeight()
      let offset = this.getVerticalOffset()

      _.each(data.yearData, (val, year) => {
        // Previous point (offcanvas)
        year = parseInt(year)

        let x = yearXMap[year]
        let y = canvasHeight * (1 - parseFloat(val.point)) + offset

        // if (!_.has(yearXMap, year) && _.has(yearXMap, year + 1)) {
        //   lineData.push(yearXMap[year + 1] - this.calculateSpaceBetweenLines())
        //   lineData.push(canvasHeight * val.point + offset)
        // }
        if (_.has(yearXMap, year)) {
          // Add hover to point
          let hoverCircle = this.canvas.circle(this.hoverSize).fill('transparent').move(x - (this.hoverSize / 2), y - (this.hoverSize / 2))
          this.hoverGroup.push(hoverCircle)
          hoverCircle.mouseover(() => {
            let mouseoverdata = []

            // Check if other data lines are too close
            // If they are we show them in the mouse poup too
            _.each(this.data, (otherline) => {
              if (Math.abs(otherline.yearData[year].point - val.point) < 0.13) {
                mouseoverdata.push({
                  label: otherline.name,
                  year: year,
                  value: this.getFixedValue(otherline.yearData[year].point)
                })
              }
            })

            EventBus.$emit('tooltip.show', {
              style: 'linechart',
              data: {
                dataarr: mouseoverdata
              }
            })
          })
          hoverCircle.mouseout(() => {
            EventBus.$emit('tooltip.hide')
          })
        }
      })
    },

    /**
     * Draws two opacity boxes
     * @return {[type]} [description]
     */
    drawOpacityBoxes () {
      let group = this.canvas.group()
      let canvasHeight = this.canvasSize[1]
      let rightBoxX = this.canvasSize[0] - this.sideSpace

      let leftBox = this.canvas.rect(this.sideSpace, canvasHeight).move(0, 0).addClass('svg-fill--white svg-fill--faded')
      let rightBox = this.canvas.rect(this.sideSpace, canvasHeight).move(rightBoxX, 0).addClass('svg-fill--white svg-fill--faded')

      group.add(leftBox, rightBox)
      this.groups.opacityBoxes = group
    },

    /**
     * Draws the data labels
     * @return {object} A SVG group containing the labels
     */
    drawLabels () {
      let group = this.canvas.group()
      let canvasHeight = this.getCalculatedCanvasHeight()
      let offset = this.getVerticalOffset()
      let fontstack = window.getComputedStyle(document.body, null).getPropertyValue('font-family')

      // Year labels
      let xmap = this.getYearXMap()
      _.each(xmap, (xPos, year) => {
        if (
          year % 5 === 0 ||
          parseInt(year) === parseInt(this.firstYear) ||
          parseInt(year) === parseInt(this.lastYear)
        ) {
          let text = this.canvas.text(year)
          text.move(xPos, this.canvasSize[1] - this.labelFontsize).font({
            anchor: 'middle',
            size: this.labelFontsize,
            family: fontstack,
            weight: 100
          })
          text.addClass('svg-fill--gray')
          group.add(text)
        }
      })

      // Y Labels
      _.each(this.yLabels, (val) => {
        let yPos = (canvasHeight - (canvasHeight * val)) + offset - (this.labelFontsize / 2) // Subtract half fontsize
        let xPos = this.sideSpace - 3
        let text = this.canvas.text(String(val))
        text.move(xPos, yPos).font({ anchor: 'end', size: this.labelFontsize, family: fontstack, weight: 100 })
        text.addClass('svg-fill--gray')
        group.add(text)
      })

      return group
    },

    addYAxisExplainer () {
      let offset = this.getVerticalOffset()
      let yPos = offset - (this.labelFontsize / 2) // Subtract half fontsize
      let xPos = this.sideSpace - 25

      let questionmark = this.canvas.select('.questionmark').first()
      questionmark.move(xPos, yPos)
      let circle = this.canvas.circle(12).attr({ 'fill': 'transparent' })
      circle.move(xPos, yPos)

      // re-add to move to top
      this.canvas.add(questionmark)
      this.canvas.add(circle)

      circle.mouseover(() => {
        EventBus.$emit('tooltip.show', {
          style: 'single',
          label: this.$Strings('yAxisExplainer')
        })
      })
      circle.mouseout(() => {
        EventBus.$emit('tooltip.hide')
      })
    },

    /**
     * Returns the canvas vertical offset
     * @return {Integer}
     */
    getVerticalOffset () {
      return this.bigStrokeWidth + 5
    },

    /**
     * Returns the calculated canvas height
     * @return {integer}
     */
    getCalculatedCanvasHeight () {
      return this.canvasSize[1] - (this.getVerticalOffset() * 2)
    },

    /**
     * Calculates the space between lines
     * @return {Float} The space in px between each line in the chart
     */
    calculateSpaceBetweenLines () {
      let calculatedWidth = this.canvasSize[0] - (this.sideSpace * 2)
      return calculatedWidth / (this.getAmountOfYears() - 1)
    },

    /**
     * Returns the amount of years shown in the graph
     * @return {Integer} The amount of years
     */
    getAmountOfYears () {
      return this.visibleRange[1] - this.visibleRange[0] + 1
    },

    /**
     * Returns an object of the years and the corresponding x value
     * @return {Object} Mapped [year] => xPosition object
     */
    getYearXMap () {
      let spaceBetween = this.calculateSpaceBetweenLines()
      let result = {}

      if (this.getAmountOfYears() === 1) {
        result[this.visibleRange[0]] = this.sideSpace
        return result
      }

      for (let i = 0; i < this.getAmountOfYears(); i++) {
        let xPos = spaceBetween * i + this.sideSpace
        let year = this.visibleRange[0] + i
        result[year] = xPos
      }

      return result
    },

    /**
     * Draw a line in the chart
     * @param  {integer} xPos      The start x position
     * @param  {integer} yPos      The start y position
     * @param  {integer} xPos2     The end x position
     * @param  {integer} yPos2     The end y position
     * @param  {String} className  The classname
     * @return {Object} The line object
     */
    drawLine (xPos, yPos, xPos2, yPos2, className, strokeWidth = 1) {
      let line = this.canvas.line(xPos, yPos, xPos2, yPos2).stroke({ width: strokeWidth })
      line.addClass(className)
      return line
    },

    /**
     * Draw a polyline in the chart
     * @param  {array} positionArray The position array
     * @param  {String} className  The classname
     * @return {Object} The line object
     */
    drawPolyline (positionArray, className, strokeWidth = 1) {
      let polyline = this.canvas
        .polyline(positionArray)
        .stroke({ width: strokeWidth })
        .fill('none')
      polyline
        .addClass(className)
      return polyline
    },

    /**
     * What to do when the mouse moves
     * @param  {object} event The JS Event object
     */
    onMouseMove (event) {
      let tooltip = document.getElementsByClassName('linechart__tooltip')
      let yPos = event.offsetY
      if (tooltip.length > 0) {
        yPos = (event.offsetY - tooltip[0].clientHeight) - 7
      }
      this.tooltipX = (event.offsetX - 2)
      this.tooltipY = yPos
    }
  }
}
</script>

<style lang="scss">
  @import '~@/components/sass/shared/colors';
  @import '~@/components/sass/placeholders/tooltip';

  .linechart{
    height: auto;
    position: relative;
    width: 100%;
  }

  .linechart__svg{
    height: calc(100% + 40px);
    margin: 0 -20px;
    overflow: hidden;
    width: calc(100% + 40px);
  }

  .linechart__label{
    font-size: 13px;
    font-weight: 500;
    text-align: center;
    margin: 25px 0;
  }

  .linechart__tooltip{
    @extend %tooltip;
    display: flex; -js-display: flex;
    flex-wrap: nowrap;
    max-width: 200px;
  }

  .linechart__tooltip__name{
    flex: 1 1 auto;
  }
  .linechart__tooltip__val{
    flex: 1 1 auto;
    margin-left: 10px;
    text-align: right;
  }

  .linechart__svg{
    .cls-1{ opacity:0.47; }
    .cls-2{
      fill:none;
      stroke:#9aa1a8;
      stroke-miterlimit:10;
    }
    .cls-3{ fill:#9aa1a8; }
  }

</style>



// WEBPACK FOOTER //
// src/components/shared/charts/LineChart.vue